import { Package, Search, Filter, RefreshCw, Printer, Clock } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Surface } from '../components/ui/Surface';
import { EmptyState } from '../components/ui/EmptyState';
import { cn } from '../utils/responsive';

import {
  useOrderManagement,
  OrderTable,
  OrderDetailsModal,
  OrderStatsDisplay,
  Order,
} from './orders';

export function OrderManagement() {
  const {
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    selectedOrder,
    setSelectedOrder,
    selectedOrderIds,
    isBatchMode,
    setIsBatchMode,
    isLoading,
    error,
    refetch,
    todayStats,
    emsInventory,
    pendingOrders,
    printedOrdersToday,
    printLabelMutation,
    batchPrintMutation,
    handleToggleSelectOrder,
    handleSelectAllPending,
    handleClearSelection,
    handleBatchPrint,
  } = useOrderManagement();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="จัดการคำสั่งซื้อ"
          description="รอคำสั่งซื้อจากลูกค้า เพื่อสั่งปริ้นใบขนส่ง"
          leading={<Package className="h-6 w-6" />}
        />
        <Surface className="flex items-center justify-center gap-3">
          <RefreshCw className="h-6 w-6 animate-spin text-yellow-500" />
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">กำลังโหลดคำสั่งซื้อ...</span>
        </Surface>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="จัดการคำสั่งซื้อ"
          description="รอคำสั่งซื้อจากลูกค้า เพื่อสั่งปริ้นใบขนส่ง"
          leading={<Package className="h-6 w-6" />}
          actions={(
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 rounded-lg border border-transparent bg-yellow-500 px-4 py-2 font-medium text-white shadow-sm transition hover:bg-yellow-600"
            >
              <RefreshCw className="h-4 w-4" />
              รีเฟรช
            </button>
          )}
        />
        <EmptyState
          icon={<Package className="h-5 w-5" />}
          title="ไม่สามารถโหลดข้อมูลคำสั่งซื้อได้"
          description="กรุณาลองใหม่อีกครั้ง หรือแจ้งทีมดูแลระบบหากปัญหายังคงอยู่"
          actions={(
            <button
              onClick={() => refetch()}
              className="btn-hyb-secondary touch-target"
            >
              <RefreshCw className="h-4 w-4" />
              ลองอีกครั้ง
            </button>
          )}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="จัดการคำสั่งซื้อ"
        description={
          isBatchMode
            ? `เลือกแล้ว ${selectedOrderIds.size} รายการ`
            : 'รอคำสั่งซื้อจากลูกค้า เพื่อสั่งปริ้นใบขนส่ง'
        }
        leading={<Package className="h-6 w-6" />}
        actions={(
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setIsBatchMode(!isBatchMode);
                handleClearSelection();
              }}
              className={cn(
                'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium shadow-sm transition-all active:scale-95 touch-target',
                isBatchMode
                  ? 'bg-stone-700 text-white hover:bg-stone-600'
                  : 'bg-blue-600 text-white hover:bg-blue-500 hover:shadow-lg'
              )}
            >
              <Printer className="h-4 w-4" />
              {isBatchMode ? 'ยกเลิก' : 'หลายรายการ'}
            </button>
            <button
              onClick={() => refetch()}
              className="btn-hyb-secondary touch-target"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        )}
      />

      {isBatchMode && (
        <Surface className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20" border={false}>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleSelectAllPending}
              className="inline-flex items-center gap-2 rounded-lg border-2 border-yellow-300 dark:border-yellow-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-yellow-700 dark:text-yellow-400 shadow-sm transition hover:bg-yellow-50 dark:hover:bg-yellow-900/30 active:scale-95 touch-target"
            >
              เลือกทั้งหมด
            </button>
            <button
              onClick={handleClearSelection}
              disabled={selectedOrderIds.size === 0}
              className="btn-hyb-secondary disabled:opacity-50 touch-target"
            >
              ยกเลิก
            </button>
          </div>
          <button
            onClick={handleBatchPrint}
            disabled={selectedOrderIds.size === 0 || batchPrintMutation.isLoading}
            className="btn-hyb-primary disabled:opacity-60 touch-target"
          >
            <Printer className="h-4 w-4 inline mr-2" />
            {batchPrintMutation.isLoading ? 'กำลังปริ้น...' : `ปริ้นใบขนส่ง (${selectedOrderIds.size})`}
          </button>
        </Surface>
      )}

      <Surface>
        <div className="flex flex-col gap-4 sm:flex-row">
          <label className="relative flex-1">
            <span className="sr-only">ค้นหาเลขที่คำสั่งซื้อ, ชื่อลูกค้า, หรือเบอร์โทร</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="ค้นหาเลขที่คำสั่งซื้อ, ชื่อลูกค้า, หรือเบอร์โทร..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-stone-300 dark:border-gray-600 bg-stone-50 dark:bg-gray-800 py-2 pl-9 pr-4 text-sm text-stone-900 dark:text-gray-100 shadow-inner focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
          </label>

          <label className="relative w-full sm:w-56">
            <span className="sr-only">กรองตามสถานะคำสั่งซื้อ</span>
            <Filter className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full appearance-none rounded-lg border border-stone-300 dark:border-gray-600 bg-stone-50 dark:bg-gray-800 py-2 pl-9 pr-10 text-sm text-stone-900 dark:text-gray-100 shadow-inner focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              <option value="all">ทุกสถานะ</option>
              <option value="pending">รอดำเนินการ</option>
              <option value="paid">ชำระแล้ว</option>
              <option value="printed">ปริ้นแล้ว</option>
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">▾</span>
          </label>
        </div>
      </Surface>

      <OrderStatsDisplay todayStats={todayStats} emsInventory={emsInventory} />

      {/* Pending Orders Section */}
      <Surface padding="none" className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              คำสั่งซื้อที่รอดำเนินการ ({pendingOrders.length})
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">แสดงทุกคำสั่งซื้อที่ยังไม่ได้ปริ้นใบขนส่ง</p>
          </div>
        </div>

        {pendingOrders.length === 0 ? (
          <EmptyState
            className="m-6"
            icon={<Clock className="h-5 w-5" />}
            title="ไม่มีคำสั่งซื้อที่รอดำเนินการ"
            description="คำสั่งซื้อใหม่จะปรากฏที่นี่"
            actions={(
              <button
                onClick={() => refetch()}
                className="btn-hyb-secondary touch-target"
              >
                <RefreshCw className="h-4 w-4 inline mr-2" />
                รีเฟรชข้อมูล
              </button>
            )}
          />
        ) : (
          <OrderTable
            orders={pendingOrders}
            isBatchMode={isBatchMode}
            selectedOrderIds={selectedOrderIds}
            onSelectOrder={handleToggleSelectOrder}
            onSelectAll={(selected: boolean) => {
              if (selected) handleSelectAllPending();
              else handleClearSelection();
            }}
            onViewDetails={(order: Order) => setSelectedOrder(order)}
            onPrintLabel={(order: Order) => printLabelMutation.mutate(order.id)}
            isPrinting={printLabelMutation.isLoading}
          />
        )}
      </Surface>

      {/* Printed Orders (Today Only) Section */}
      <Surface padding="none" className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              คำสั่งซื้อที่ปริ้นแล้ว (วันนี้) ({printedOrdersToday.length})
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">แสดงเฉพาะคำสั่งซื้อที่ปริ้นใบขนส่งแล้ววันนี้</p>
          </div>
        </div>

        {printedOrdersToday.length === 0 ? (
          <div className="text-center py-8 text-stone-500 dark:text-stone-400 text-sm">
            ยังไม่มีคำสั่งซื้อที่ปริ้นแล้ววันนี้
          </div>
        ) : (
          <OrderTable
            orders={printedOrdersToday}
            isBatchMode={false}
            selectedOrderIds={new Set()}
            onSelectOrder={() => { }}
            onSelectAll={() => { }}
            onViewDetails={(order: Order) => setSelectedOrder(order)}
            onPrintLabel={(order: Order) => printLabelMutation.mutate(order.id)}
            isPrinting={printLabelMutation.isLoading}
            emptyMessage="ยังไม่มีคำสั่งซื้อที่ปริ้นแล้ววันนี้"
          />
        )}
      </Surface>

      <OrderDetailsModal
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onPrintLabel={(order: Order) => printLabelMutation.mutate(order.id)}
        isPrinting={printLabelMutation.isLoading}
      />
    </div>
  );
}
